import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, useMediaQuery } from '@mui/material';

import { LoadingSkeleton } from '@components/LoadingSkeleton';
import { useMasterScriptsQuery } from '@features/scripts/hooks/useScripts';
import {
  useRackAssignmentsQuery,
  useUpsertRackAssignments
} from '@api/bff/rackAssignments/useRackAssignments';
import { useRackGrid, useRackDnd, useClickSwap } from './hooks';
import { useRackScriptResolution } from '../../hooks/useRackScriptResolution';
import { formatPositionLabel } from './utils';
import { RACK_TOTAL } from './constants';
import {
  RackSummaryBar,
  CellDetailModal,
  DesktopRackGrid,
  MobileRackList
} from './components';

import type { RackCellAssignmentDto } from '@api/bff/rackAssignments/useRackAssignments';
import type { WorkOrderDetailViewModel } from '@api/enterprise/openapi/models';
import type { Script } from '@features/scripts/types';
import type { RackCell } from './types';

const SAVE_DEBOUNCE_MS = 1000;

const serializeCells = (cells: RackCellAssignmentDto[]): string =>
  JSON.stringify(
    [...cells]
      .map(cell => ({
        cell: cell.cell,
        equipmentRef: cell.equipmentRef,
        scriptGlobalId: cell.scriptGlobalId,
        overridden: cell.overridden
      }))
      .sort((a, b) => a.cell.localeCompare(b.cell))
  );

type Props = {
  jobNumber: string;
  details: WorkOrderDetailViewModel[];
  isLoading: boolean;
  isReadOnly?: boolean;
  onDetailClick: (globalId: string) => void;
  onSkipClick: (detail: WorkOrderDetailViewModel) => void;
  onUnskipClick: (detail: WorkOrderDetailViewModel) => void;
};

const VoltmeterRack: React.FC<Props> = ({
  jobNumber,
  details,
  isLoading,
  isReadOnly = false,
  onDetailClick,
  onSkipClick,
  onUnskipClick
}) => {
  const { t } = useTranslation('workOrderDetails');
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const [overrides, setOverrides] = useState<Map<number, Script>>(new Map());

  const { grid, flatCells, stats } = useRackGrid(details);

  const overflowDetails = useMemo(
    () =>
      details.filter(detail => {
        const seq = detail.sequenceNumber;
        return seq == null || seq < 1 || seq > RACK_TOTAL;
      }),
    [details]
  );

  const overflowLabels = useMemo(
    () =>
      overflowDetails
        .map(detail => (detail.id ?? '').trim())
        .filter(id => id !== '')
        .join(', '),
    [overflowDetails]
  );
  const { resolutionBySequence, isLoading: isResolutionLoading } =
    useRackScriptResolution(details);

  const { data: scriptsResponse } = useMasterScriptsQuery();
  const masterScripts = useMemo(
    () => scriptsResponse?.data ?? [],
    [scriptsResponse]
  );

  const { data: persisted } = useRackAssignmentsQuery(jobNumber, !!jobNumber);
  const upsertAssignments = useUpsertRackAssignments(jobNumber);

  const hydratedRef = useRef(false);
  const lastSavedRef = useRef<string | null>(null);

  const resolveCell = useCallback(
    (cell: RackCell): RackCell => {
      const auto = resolutionBySequence.get(cell.sequenceNumber) ?? null;
      const override = overrides.get(cell.sequenceNumber) ?? null;
      return {
        ...cell,
        resolution: {
          equipment: auto?.equipment ?? null,
          resolvedScript: override ?? auto?.resolvedScript ?? null,
          overridden: override !== null
        }
      };
    },
    [resolutionBySequence, overrides]
  );

  const enrichedGrid = useMemo(
    () => grid.map(row => row.map(resolveCell)),
    [grid, resolveCell]
  );
  const enrichedFlatCells = useMemo(
    () => flatCells.map(resolveCell),
    [flatCells, resolveCell]
  );

  const activeCell = useMemo(
    () =>
      selectedSeq === null
        ? null
        : enrichedFlatCells.find(cell => cell.sequenceNumber === selectedSeq) ??
          null,
    [selectedSeq, enrichedFlatCells]
  );

  const handleOverride = useCallback(
    (sequenceNumber: number, script: Script) => {
      setOverrides(prev => {
        const next = new Map(prev);
        next.set(sequenceNumber, script);
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (!persisted || hydratedRef.current) return;

    const hasOverriddenCells = persisted.cells.some(cell => cell.overridden);
    if (hasOverriddenCells && masterScripts.length === 0) return;

    const sequenceByCellLabel = new Map<string, number>();
    details.forEach(detail => {
      if (detail.sequenceNumber != null) {
        sequenceByCellLabel.set(
          formatPositionLabel(detail.sequenceNumber),
          detail.sequenceNumber
        );
      }
    });

    const hydrated = new Map<number, Script>();
    persisted.cells.forEach(cell => {
      if (!cell.overridden) return;
      const sequenceNumber = sequenceByCellLabel.get(cell.cell);
      if (sequenceNumber == null) return;
      const script = masterScripts.find(
        candidate =>
          candidate.globalId.trim().toLowerCase() ===
          cell.scriptGlobalId.trim().toLowerCase()
      );
      if (script) hydrated.set(sequenceNumber, script);
    });

    if (hydrated.size > 0) setOverrides(hydrated);
    lastSavedRef.current = serializeCells(persisted.cells);
    hydratedRef.current = true;
  }, [persisted, masterScripts, details]);

  const desiredCells = useMemo<RackCellAssignmentDto[]>(
    () =>
      enrichedFlatCells
        .filter(
          cell =>
            cell.detail &&
            (cell.detail.id ?? '').trim() !== '' &&
            cell.resolution?.resolvedScript != null
        )
        .map(cell => ({
          cell: formatPositionLabel(cell.sequenceNumber),
          equipmentRef: (cell.detail?.id ?? '').trim(),
          scriptGlobalId: cell.resolution!.resolvedScript!.globalId,
          overridden: cell.resolution!.overridden
        })),
    [enrichedFlatCells]
  );

  const desiredJson = useMemo(
    () => serializeCells(desiredCells),
    [desiredCells]
  );

  useEffect(() => {
    if (!jobNumber || isReadOnly) return;
    if (!hydratedRef.current || isResolutionLoading) return;
    if (lastSavedRef.current === desiredJson) return;

    const timer = setTimeout(() => {
      upsertAssignments.mutate(
        { cells: desiredCells },
        {
          onSuccess: () => {
            lastSavedRef.current = desiredJson;
          }
        }
      );
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desiredJson, jobNumber, isReadOnly, isResolutionLoading]);

  const handleSwap = useCallback((_sourceSeq: number, _targetSeq: number) => {
    // Swap is a no-op until backed by server persistence
  }, []);

  const {
    sensors,
    activeId,
    collisionDetection,
    handleDragStart,
    handleDragEnd
  } = useRackDnd({ onSwap: handleSwap });

  const { selectedCell: selectedSwapCell, handleCellSelect } = useClickSwap({
    onSwap: handleSwap
  });

  const handleCellClick = useCallback(
    (cell: RackCell) => {
      if (!isReadOnly) {
        handleCellSelect(cell.sequenceNumber);
      }
      setSelectedSeq(cell.sequenceNumber);
    },
    [isReadOnly, handleCellSelect]
  );

  const handleCloseModal = useCallback(() => {
    setSelectedSeq(null);
  }, []);

  if (isLoading) {
    return <LoadingSkeleton variant="grid" lines={20} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <RackSummaryBar stats={stats} />

      {overflowDetails.length > 0 && (
        <Alert severity="warning" variant="outlined">
          {t('rack.overflowNotice', { count: overflowDetails.length })}
          {overflowLabels && ` (${overflowLabels})`}
        </Alert>
      )}

      {isMobile ? (
        <MobileRackList
          flatCells={enrichedFlatCells}
          onCellClick={handleCellClick}
        />
      ) : (
        <DesktopRackGrid
          grid={enrichedGrid}
          flatCells={enrichedFlatCells}
          onCellClick={handleCellClick}
          selectedCell={selectedSwapCell}
          activeId={activeId}
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          isReadOnly={isReadOnly}
        />
      )}

      <CellDetailModal
        open={activeCell !== null}
        onClose={handleCloseModal}
        cell={activeCell}
        onDetailClick={onDetailClick}
        onSkipClick={onSkipClick}
        onUnskipClick={onUnskipClick}
        onOverride={handleOverride}
        isReadOnly={isReadOnly}
      />
    </Box>
  );
};

export default VoltmeterRack;
